/**
 * The AI control panel: usage, credits, models and skills in one place.
 *
 * They were once four sidebar entries — half of Super Admin's menu — for
 * four views of one subsystem: what the assistant spent, what it costs, which
 * model answers, and what that model knows. This is the shell that holds them,
 * on the /settings/:tab pattern so a master who has used that screen already
 * knows this one: a pill nav, and one panel at a time.
 *
 * A fifth tab, ad review, held the knobs of the ad review panel on /insights.
 * It left with that page (hidden since 2026-10-16; the tab since 2026-10-20,
 * owner). AdReviewSettings.tsx, its service and its table are kept for when
 * /insights comes back, and an old /admin/ai/ad-review link opens usage
 * through the unknown-tab fallback below.
 *
 * Unlike Settings the tabs sit ABOVE the page heading rather than below it,
 * because each panel keeps its own hero — and two of those heroes carry working
 * controls (a refresh button on usage, the credits-disabled badge). Lifting the
 * heading into this shell would have thrown those away, so the shell adds a tab
 * strip and changes nothing inside the panels.
 *
 * Only the selected panel is rendered. Each loads its own data on mount, so
 * rendering them together would fire four sets of queries to show one of them —
 * the reason Settings does the same.
 *
 * The original four keep their own routes as redirects rather than losing them:
 * two places in the app link here by URL, and a master may have bookmarked one.
 *
 * @module pages/admin/AiHub
 */

import React, { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Activity, Bot, Coins, Cpu } from "lucide-react";
import clsx from "clsx";
import AdminAiUsage from "./AiUsage";
import AdminCredits from "./Credits";
import { AdminAiModels } from "./AiModels";
import { AdminAgentProfiles } from "./AgentProfiles";

export type AiTabId = "usage" | "credits" | "models" | "skills";

interface AiTab {
  id: AiTabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Ordered as a master reads them: what happened, what it cost, then the
 * settings that decide both.
 */
export const AI_TABS: AiTab[] = [
  { id: "usage", label: "Usage", icon: Activity },
  { id: "credits", label: "Credits", icon: Coins },
  { id: "models", label: "Models", icon: Cpu },
  { id: "skills", label: "Skills", icon: Bot },
];

const isTabId = (value: string | undefined): value is AiTabId =>
  AI_TABS.some((t) => t.id === value);

export const AdminAiHub: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  // An unknown tab in the URL lands on the first one rather than an empty
  // screen: a mistyped or renamed link should still show something usable.
  const [activeTab, setActiveTab] = useState<AiTabId>(isTabId(tab) ? tab : "usage");

  const handleTabChange = useCallback(
    (next: AiTabId) => {
      setActiveTab(next);
      // replace, so the back button leaves the AI screen instead of walking
      // back through every tab the reader looked at.
      navigate(`/admin/ai/${next}`, { replace: true });
    },
    [navigate],
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <nav className="flex flex-wrap gap-2" aria-label="AI settings">
        {AI_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => handleTabChange(t.id)}
            aria-current={activeTab === t.id ? "page" : undefined}
            className={clsx(
              "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors border",
              activeTab === t.id
                ? "bg-ink-950 border-ink-950 text-white"
                : "bg-white border-neutral-200 text-neutral-700 hover:text-ink-900 hover:border-neutral-300",
            )}
          >
            <t.icon className="w-4 h-4" />
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      {activeTab === "usage" && <AdminAiUsage />}
      {activeTab === "credits" && <AdminCredits />}
      {activeTab === "models" && <AdminAiModels />}
      {activeTab === "skills" && <AdminAgentProfiles />}
    </div>
  );
};

export default AdminAiHub;
